import React, { lazy, Suspense, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import ProtectedRoute from "./routes/ProtectedRoute";
import { getDefaultRouteByRole } from "./utils/navigation";
import { ROLES } from "./utils/roles";
import { readSession } from "./services/session";

const AcademicStaffPage = lazy(() => import("./pages/AcademicStaffPage"));
const AcademicActivitiesPage = lazy(() => import("./pages/AcademicActivitiesPage"));
const AcademicReportPage = lazy(() => import("./pages/AcademicReportPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RectorDashboardPage = lazy(() => import("./pages/RectorDashboardPage"));
const PerformanceCenterPage = lazy(() => import("./pages/PerformanceCenterPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));

function RouteLoading() {
  return (
    <main className="route-loading" role="status" aria-live="polite">
      <span className="route-loading-spinner" aria-hidden="true" />
      <span>Sayfa hazırlanıyor...</span>
    </main>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    return readSession()?.user || null;
  });



  const defaultRoute = getDefaultRouteByRole(currentUser?.role);

  return (
    <Router>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
        <Route
          path="/login"
          element={<LoginPage setCurrentUser={setCurrentUser} />}
        />

        <Route path="/" element={<Navigate to={defaultRoute} replace />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[ROLES.ADMIN]}
              redirectTo="/login"
            >
              <AdminPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/academic"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[
                ROLES.ACADEMIC,
                ROLES.DEPARTMENT_HEAD,
                ROLES.DEAN,
                ROLES.RECTOR,
              ]}
              redirectTo="/login"
            >
              <AcademicStaffPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/academic-activities"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[
                ROLES.ACADEMIC,
                ROLES.DEPARTMENT_HEAD,
                ROLES.DEAN,
                ROLES.LIBRARY,
              ]}
              redirectTo="/login"
            >
              <AcademicActivitiesPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/academic-report"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[
                ROLES.ACADEMIC,
                ROLES.DEPARTMENT_HEAD,
                ROLES.DEAN,
                ROLES.RECTOR,
              ]}
              redirectTo="/login"
            >
              <AcademicReportPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route path="/department-performance" element={<Navigate to="/academic-report" replace />} />
        <Route path="/unit-performance" element={<Navigate to="/academic-report" replace />} />
        <Route path="/university-performance" element={<Navigate to="/academic-report" replace />} />
        <Route
          path="/performance-center"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[ROLES.RECTOR]}
              redirectTo="/login"
            >
              <PerformanceCenterPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/rector-dashboard"
          element={
            <ProtectedRoute
              userRole={currentUser?.role}
              allowedRoles={[ROLES.RECTOR]}
              redirectTo="/login"
            >
              <RectorDashboardPage currentUser={currentUser} />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
